import React, { useState, useRef } from 'react';
import { 
  Combine, 
  Minus, 
  BoxSelect, 
  Layers, 
  Scissors, 
  Grid, 
  Split, 
  X, 
  Sparkles, 
  Check, 
  GripVertical, 
  Eye, 
  Wand2, 
  Spline,
  Flame,
  Maximize2
} from 'lucide-react';
import { VdpElement } from '../../types';
import { applyPathfinderOperation } from '../../utils/pathfinderUtils';
import { 
  PathfinderUnionIcon, 
  PathfinderSubtractIcon, 
  PathfinderIntersectIcon, 
  PathfinderExcludeIcon 
} from './PathfinderIcons';
import { SimplifyPathIcon } from './SimplifyPathIcon';

interface PathfinderPanelProps {
  isOpen: boolean;
  onClose: () => void;
  selectedElements: VdpElement[];
  allElements: VdpElement[];
  setTemplateElements: (elements: VdpElement[]) => void;
  setSelectedElementIds: (ids: string[]) => void;
  onOpenSimplifyPath?: () => void;
}

export const PathfinderPanel: React.FC<PathfinderPanelProps> = ({
  isOpen,
  onClose,
  selectedElements,
  allElements,
  setTemplateElements,
  setSelectedElementIds,
  onOpenSimplifyPath,
}) => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const count = selectedElements.length;
  const canApply = count >= 2;

  const handlePointerDownDrag = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!panelRef.current) return;

    const rect = panelRef.current.getBoundingClientRect();
    const currX = position ? position.x : rect.left;
    const currY = position ? position.y : rect.top;

    if (!position) {
      setPosition({ x: rect.left, y: rect.top });
    }

    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: currX,
      initialY: currY,
    };
    setIsDragging(true);

    const handlePointerMove = (moveEvt: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = moveEvt.clientX - dragRef.current.startX;
      const dy = moveEvt.clientY - dragRef.current.startY;
      setPosition({
        x: Math.max(10, Math.min(window.innerWidth - 320, dragRef.current.initialX + dx)),
        y: Math.max(10, Math.min(window.innerHeight - 380, dragRef.current.initialY + dy)),
      });
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      dragRef.current = null;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleOp = (mode: 'unite' | 'subtract' | 'intersect' | 'exclude' | 'divide' | 'merge' | 'minus-back' | 'trim' | 'crop' | 'outline') => {
    if (!canApply) return;
    const updated = applyPathfinderOperation(selectedElements, allElements, mode);
    setTemplateElements(updated);
    const newEl = updated.find(e => e.id.startsWith('pathfinder-'));
    if (newEl) {
      setSelectedElementIds([newEl.id]);
    }
  };

  const handleConvertToCutline = () => {
    if (selectedElements.length === 0) return;
    const selectedIds = new Set(selectedElements.map(e => e.id));
    const updated = allElements.map(el => {
      if (selectedIds.has(el.id)) {
        return {
          ...el,
          isCutline: true,
          borderColor: '#ec4899', // Pink Cut Contour
          strokeColor: '#ec4899',
          strokeWidth: 2,
          lineDash: 'dashed',
          backgroundColor: 'transparent',
          isTransparentBg: true
        };
      }
      return el;
    });
    setTemplateElements(updated);
  };

  const handleSelectAll = () => {
    setSelectedElementIds(allElements.map(e => e.id));
  };

  return (
    <div
      ref={panelRef}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={
        position
          ? {
              position: 'fixed',
              left: `${position.x}px`,
              top: `${position.y}px`,
              zIndex: 99999,
            }
          : {
              position: 'fixed',
              right: '24px',
              top: '120px',
              zIndex: 99999,
            }
      }
      className="w-80 bg-zinc-900/95 backdrop-blur-xl border border-zinc-700/80 rounded-2xl shadow-2xl p-4 text-zinc-100 font-sans select-none animate-in fade-in zoom-in-95 duration-150 pointer-events-auto"
    >
      {/* Header / Drag Bar */}
      <div className="flex items-center justify-between pb-3 border-b border-zinc-800 mb-3">
        <div 
          onPointerDown={handlePointerDownDrag}
          className="flex items-center gap-2 cursor-grab active:cursor-grabbing text-zinc-300 hover:text-white transition-colors"
        >
          <GripVertical className="w-4 h-4 text-zinc-500" />
          <div className="flex items-center gap-1.5">
            <Combine className="w-4 h-4 text-indigo-400" />
            <span className="font-semibold text-sm tracking-wide">Pathfinder Tool</span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors cursor-pointer"
          title="Close Pathfinder Panel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Selection Status Badge */}
      <div className={`mb-3.5 px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-between transition-all ${
        canApply 
          ? 'bg-indigo-950/60 border border-indigo-500/30 text-indigo-300' 
          : 'bg-amber-950/40 border border-amber-500/20 text-amber-300'
      }`}>
        <div className="flex items-center gap-2">
          <BoxSelect className="w-4 h-4" />
          <span>{canApply ? `${count} elements selected` : count === 1 ? 'Select 1 more element to combine' : 'Select 2+ elements to apply'}</span>
        </div>
        {canApply ? (
          <span className="bg-indigo-500 text-white font-bold text-[10px] px-2 py-0.5 rounded-full">
            Ready
          </span>
        ) : allElements.length >= 2 ? (
          <button
            type="button"
            onClick={handleSelectAll}
            className="bg-amber-500 hover:bg-amber-400 text-black font-bold text-[10px] px-2 py-0.5 rounded-full transition-colors cursor-pointer shrink-0"
            title="Select all canvas shapes"
          >
            Select All ({allElements.length})
          </button>
        ) : null}
      </div>

      {/* Section 1: Shape Modes */}
      <div className="mb-4">
        <div className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5 text-indigo-400" />
          <span>Shape Modes</span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {/* Unite / Union */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('unite')}
            className="group flex flex-col items-center gap-1.5 p-2 rounded-xl bg-zinc-800/60 border border-zinc-700/60 hover:border-indigo-500 hover:bg-indigo-600/20 transition-all disabled:opacity-40 disabled:hover:border-zinc-700 disabled:hover:bg-zinc-800/60 cursor-pointer disabled:cursor-not-allowed"
            title="Unite: Combines all selected shapes into a single vector path"
          >
            <div className="w-7 h-7 rounded-lg bg-zinc-900 flex items-center justify-center group-hover:bg-indigo-600/30 text-indigo-400">
              <PathfinderUnionIcon className="w-4 h-4" />
            </div>
            <span className="text-[9px] font-medium text-zinc-300">Unite</span>
          </button>

          {/* Minus Front / Subtract */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('subtract')}
            className="group flex flex-col items-center gap-1.5 p-2 rounded-xl bg-zinc-800/60 border border-zinc-700/60 hover:border-indigo-500 hover:bg-indigo-600/20 transition-all disabled:opacity-40 disabled:hover:border-zinc-700 disabled:hover:bg-zinc-800/60 cursor-pointer disabled:cursor-not-allowed"
            title="Minus Front: Subtracts top overlapping shapes from the bottom shape"
          >
            <div className="w-7 h-7 rounded-lg bg-zinc-900 flex items-center justify-center group-hover:bg-indigo-600/30 text-rose-400">
              <PathfinderSubtractIcon className="w-4 h-4" />
            </div>
            <span className="text-[9px] font-medium text-zinc-300">Subtract</span>
          </button>

          {/* Intersect */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('intersect')}
            className="group flex flex-col items-center gap-1.5 p-2 rounded-xl bg-zinc-800/60 border border-zinc-700/60 hover:border-indigo-500 hover:bg-indigo-600/20 transition-all disabled:opacity-40 disabled:hover:border-zinc-700 disabled:hover:bg-zinc-800/60 cursor-pointer disabled:cursor-not-allowed"
            title="Intersect: Keeps only the overlapping areas of selected shapes"
          >
            <div className="w-7 h-7 rounded-lg bg-zinc-900 flex items-center justify-center group-hover:bg-indigo-600/30 text-amber-400">
              <PathfinderIntersectIcon className="w-4 h-4" />
            </div>
            <span className="text-[9px] font-medium text-zinc-300">Intersect</span>
          </button>

          {/* Exclude / XOR */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('exclude')}
            className="group flex flex-col items-center gap-1.5 p-2 rounded-xl bg-zinc-800/60 border border-zinc-700/60 hover:border-indigo-500 hover:bg-indigo-600/20 transition-all disabled:opacity-40 disabled:hover:border-zinc-700 disabled:hover:bg-zinc-800/60 cursor-pointer disabled:cursor-not-allowed"
            title="Exclude (XOR): Keeps non-overlapping areas and creates cutouts where shapes overlap"
          >
            <div className="w-7 h-7 rounded-lg bg-zinc-900 flex items-center justify-center group-hover:bg-indigo-600/30 text-emerald-400">
              <PathfinderExcludeIcon className="w-4 h-4" />
            </div>
            <span className="text-[9px] font-medium text-zinc-300">Exclude</span>
          </button>

          {/* Minus Back */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('minus-back')}
            className="group flex flex-col items-center gap-1.5 p-2 rounded-xl bg-zinc-800/60 border border-zinc-700/60 hover:border-indigo-500 hover:bg-indigo-600/20 transition-all disabled:opacity-40 disabled:hover:border-zinc-700 disabled:hover:bg-zinc-800/60 cursor-pointer disabled:cursor-not-allowed"
            title="Minus Back: Subtracts bottom shapes from the top shape"
          >
            <div className="w-7 h-7 rounded-lg bg-zinc-900 flex items-center justify-center group-hover:bg-indigo-600/30 text-orange-400">
              <Minus className="w-3.5 h-3.5" />
            </div>
            <span className="text-[9px] font-medium text-zinc-300">Minus Back</span>
          </button>
        </div>
      </div>

      {/* Section 2: Pathfinders */}
      <div className="mb-4">
        <div className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Scissors className="w-3.5 h-3.5 text-purple-400" />
          <span>Pathfinder Operations</span>
        </div>
        <div className="grid grid-cols-3 gap-1.5 mb-1.5">
          {/* Divide */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('divide')}
            className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800 hover:border-purple-500/50 text-xs font-medium text-zinc-200 transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
            title="Divide: Separates overlapping geometry into distinct sub-paths"
          >
            <Scissors className="w-3.5 h-3.5 text-purple-400" />
            <span>Divide</span>
          </button>

          {/* Trim */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('trim')}
            className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800 hover:border-cyan-500/50 text-xs font-medium text-zinc-200 transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
            title="Trim: Removes hidden geometry beneath top shapes"
          >
            <BoxSelect className="w-3.5 h-3.5 text-cyan-400" />
            <span>Trim</span>
          </button>

          {/* Merge */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('merge')}
            className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800 hover:border-indigo-500/50 text-xs font-medium text-zinc-200 transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
            title="Merge: Join touching shapes together"
          >
            <Combine className="w-3.5 h-3.5 text-indigo-400" />
            <span>Merge</span>
          </button>
        </div>

        <div className="grid grid-cols-3 gap-1.5">
          {/* Crop */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('crop')}
            className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800 hover:border-amber-500/50 text-xs font-medium text-zinc-200 transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
            title="Crop: Clips shapes using the frontmost object as a boundary"
          >
            <Maximize2 className="w-3.5 h-3.5 text-amber-400" />
            <span>Crop</span>
          </button>

          {/* Outline */}
          <button
            disabled={!canApply}
            onClick={() => handleOp('outline')}
            className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800 hover:border-teal-500/50 text-xs font-medium text-zinc-200 transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
            title="Outline: Converts shape fills to stroke line segments"
          >
            <Spline className="w-3.5 h-3.5 text-teal-400" />
            <span>Outline</span>
          </button>

          {/* Convert to Cutline */}
          <button
            disabled={count === 0}
            onClick={handleConvertToCutline}
            className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-xl bg-zinc-800/50 border border-zinc-700/50 hover:bg-zinc-800 hover:border-pink-500/50 text-xs font-medium text-pink-300 transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
            title="Convert selected elements into a Plotter Die-Cut Contour line (Pink)"
          >
            <Flame className="w-3.5 h-3.5 text-pink-400" />
            <span>Die-Cut</span>
          </button>
        </div>
      </div>

      {/* Section 3: Path Optimization & Simplification */}
      <div className="mb-4">
        <div className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <SimplifyPathIcon className="w-3.5 h-3.5 text-emerald-400" />
            <span>Path Optimization</span>
          </div>
          <span className="text-[9px] text-zinc-500 font-mono">Reduce Nodes</span>
        </div>
        <button
          disabled={count === 0}
          onClick={() => {
            if (onOpenSimplifyPath) {
              onOpenSimplifyPath();
            }
          }}
          className="w-full flex items-center justify-between py-2 px-3 rounded-xl bg-emerald-950/40 border border-emerald-500/40 hover:bg-emerald-900/50 hover:border-emerald-400 text-xs font-medium text-emerald-200 transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed group shadow-xs"
          title="Simplify Path: Reduce anchor points & eliminate jitter while preserving curve integrity"
        >
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center group-hover:scale-105 transition-transform">
              <SimplifyPathIcon className="w-3.5 h-3.5" />
            </div>
            <div className="text-left">
              <div className="font-semibold text-emerald-200 leading-tight">Simplify Path...</div>
              <div className="text-[10px] text-emerald-400/80">Reduce points & smooth bezier curves</div>
            </div>
          </div>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-semibold border border-emerald-500/30">
            Clean Up
          </span>
        </button>
      </div>

      {/* Quick Info Footer */}
      <div className="pt-2 border-t border-zinc-800 text-[11px] text-zinc-400 flex items-center justify-between">
        <span className="flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-amber-400" /> Precision Vector Engine
        </span>
        <button
          onClick={onClose}
          className="text-xs text-indigo-400 hover:text-indigo-300 font-medium hover:underline cursor-pointer"
        >
          Done
        </button>
      </div>
    </div>
  );
};
